import { Table } from 'antd';
import './VtxTable.scss';
import './../pagination/VtxPagination.scss';
import * as React from 'react';
import classNames from 'classnames';
import { TableProps, ColumnProps } from 'antd/lib/table';
import { mergePaginationOptions, VtxPaginationConfig } from './../pagination/VtxPagination';
import {
    SorterResult,
    TableCurrentDataSource,
    TablePaginationConfig,
    ExpandableConfig,
} from 'antd/lib/table/interface';
import { IAutomatable, preventDataAutomationOverriding } from '../../helpers/IAutomatable';
import { PaginationConfig } from 'antd/lib/pagination';
import { ChildrenKey, IMightHaveChildren } from './helpers/treeTypes';
import { assertNever } from '../../helpers/neverCheckers';

type KeyGetter<T> = (record: T, index?: number) => React.Key;

export interface ExpandableButNotTree<T extends object>
    extends Omit<ExpandableConfig<T>, 'childrenColumnName'> {
    /**
     * @deprecated
     * If you're looking to have children in a table, please use VtxTreeTable instead
     */
    childrenColumnName?: ChildrenKey;
}

export interface VtxTableProps<T extends object>
    extends Omit<TableProps<T>, 'pagination' | 'onChange' | 'children' | 'childrenColumnName'>,
        IAutomatable {
    /**
     * You can set this to the props of VtxPagination. Undefined will set this to the default pagination settings. If you don't want pagination, use false or consider setting 'pagination.hideOnSinglePage' to true instead.
     */
    pagination?: VtxPaginationConfig | false;

    onChange?: (
        pagination: VtxPaginationConfig,
        filters: Record<string, (string | number | boolean)[] | null>,
        sorter: SorterResult<T> | SorterResult<T>[],
        extra: TableCurrentDataSource<T>
    ) => void;
    /**
     * Row's unique key, could be a string or function that returns a string
     * @default "key"
     */
    rowKey?: string | KeyGetter<T>;
    /**
     * If you're looking to have children in a table, please consider using VtxTreeTable instead
     */
    children?: undefined;
    /**
     * @deprecated
     * If you're looking to have children in a table, please use VtxTreeTable instead
     */
    childrenColumnName?: ChildrenKey;
    /**
     * A configuration object that allows you to control the expand icon and it's functionality
     */
    expandable?: ExpandableButNotTree<T>;
    /**
     * Since developers should be using VtxTreeTable if they have hierarchical data, the default functionality will console.warn if children are present. You can turn off this warning.
     * @default true
     */
    warnOnPresenceOfChildren?: boolean;
    /**
     * Similar to a pre-set theme for the table
     */
    styleMode?: 'banded' | 'light';
}

export type TableStyleMode = VtxTableProps<{}>['styleMode'];

export type VtxColumnProps<T extends object> = ColumnProps<T>;

const paginationConfigMassager = (
    input: PaginationConfig,
    positionTheWayThatTablesLikeIt: TablePaginationConfig['position']
): TablePaginationConfig => {
    return Object.assign({}, input, {
        position: positionTheWayThatTablesLikeIt,
    });
};

const getStyleModeClassname = (styleMode: VtxTableProps<{}>['styleMode']) => {
    if (!styleMode || styleMode === 'banded') {
        return 'vtx-table-style-mode-banded';
    } else if (styleMode === 'light') {
        return 'vtx-table-style-mode-light';
    } else {
        return assertNever(styleMode);
    }
};

export const VtxTable = <T extends object>(props: VtxTableProps<T>) => {
    const automationClass = 'vtx-table';

    const {
        className,
        pagination,
        styleMode,
        onChange,
        rowKey,
        ...otherProps
    } = preventDataAutomationOverriding(props);

    const styleModeClassname = getStyleModeClassname(styleMode);

    const classNamesCombined = classNames(automationClass, className, styleModeClassname);

    if (props.warnOnPresenceOfChildren) {
        if (props.dataSource) {
            for (const iterator of props.dataSource) {
                const innocentUntilProvenGuilty = iterator as IMightHaveChildren<object>;
                if (innocentUntilProvenGuilty.children) {
                    const childrenKey: ChildrenKey = 'children';
                    const warnOnPresenceOfChildrenKeyName: keyof VtxTableProps<{}> =
                        'warnOnPresenceOfChildren';
                    // eslint-disable-next-line no-console
                    console.warn(
                        `dataSource was found to contain ${childrenKey}. Did you mean to use VtxTreeTable instead? If the data just happened to have a property called ${childrenKey}, you can turn off this warning by setting ${warnOnPresenceOfChildrenKeyName}`
                    );
                }
            }
        }
    }

    let onChangeWrapper: TableProps<T>['onChange'] = undefined;
    if (onChange) {
        onChangeWrapper = (pagination, filters, sorter, extra) => {
            return onChange(pagination as VtxPaginationConfig, filters, sorter, extra);
        };
    }
    const paginationMerged = mergePaginationOptions(pagination);
    const finalProps: TableProps<T> = {
        ...otherProps,
        rowKey: rowKey ? rowKey : 'key',
        onChange: onChangeWrapper,
        pagination: pagination === false ? false : paginationConfigMassager(paginationMerged, ['bottomLeft']),
        className: classNamesCombined,
    };

    const actualTableElem = Table<T>(finalProps);
    return (
        <div data-automation-class={automationClass} className={classNamesCombined}>
            {actualTableElem}
        </div>
    );
};
